import { useCallback, useEffect, useMemo, useState } from 'react'
import { Mail, RefreshCw, Activity, CheckCircle2, XCircle, Send, Loader2, AlertTriangle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import { DataTable, DataTableColumn } from '@/components/admin/DataTable'
import { apiClient } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'

// Admin Panel Redesign — Emails section.
// (1) SMTP health + test email — ported as-is from the old AdminPanel.tsx
//     "system" tab (that tab is dropping this card in a parallel task; it
//     lives only here now).
// (2) Compose Broadcast — two-step preview/confirm flow against the new
//     admin email-broadcast endpoint.
// (3) Email Activity Log — paginated table over every email the backend's
//     EmailService has sent since this session's instrumentation shipped.

interface EmailStatus {
  configured: boolean
  host: string
  port: number
  from: string
  use_ssl: boolean
  use_tls: boolean
  message: string
}

interface EmailLogRow {
  id: string
  to_email: string
  subject: string
  template: string | null
  triggered_by_user_id: string | null
  success: boolean
  error: string | null
  sent_at: string
}

type BroadcastPreview = { recipient_count: number; sample_recipients: string[]; exceeds_cap: boolean }
type BroadcastResult = { sent: string[]; failed: { email: string; error: string }[] }

type FilterMode = 'all' | 'tier' | 'org' | 'users'
type SuccessFilter = 'all' | 'success' | 'failed'

const LOG_PAGE_SIZE = 25

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

export default function EmailsSection() {
  const { toast } = useToast()

  // ---- SMTP health ----
  const [emailStatus, setEmailStatus] = useState<EmailStatus | null>(null)
  const [emailStatusLoading, setEmailStatusLoading] = useState(false)
  const [emailStatusError, setEmailStatusError] = useState<string | null>(null)
  const [emailTestLoading, setEmailTestLoading] = useState(false)

  const loadEmailStatus = useCallback(async () => {
    setEmailStatusLoading(true)
    setEmailStatusError(null)
    try {
      const status = await apiClient.getEmailStatus()
      setEmailStatus(status)
    } catch (e: any) {
      console.error('Failed to load email status', e)
      setEmailStatusError(e.message || 'Failed to load email status')
    } finally {
      setEmailStatusLoading(false)
    }
  }, [])

  // ---- Email activity log ----
  const [logs, setLogs] = useState<EmailLogRow[]>([])
  const [logsTotal, setLogsTotal] = useState(0)
  const [logsLoading, setLogsLoading] = useState(false)
  const [logsError, setLogsError] = useState<string | null>(null)
  const [logSkip, setLogSkip] = useState(0)

  // Filter inputs (draft) vs. applied filters (what was actually fetched)
  const [toEmailInput, setToEmailInput] = useState('')
  const [templateInput, setTemplateInput] = useState('')
  const [successFilter, setSuccessFilter] = useState<SuccessFilter>('all')
  const [appliedFilters, setAppliedFilters] = useState<{ to_email: string; template: string; success: SuccessFilter }>({
    to_email: '',
    template: '',
    success: 'all',
  })

  const loadLogs = useCallback(async (skip: number, filters: { to_email: string; template: string; success: SuccessFilter }) => {
    setLogsLoading(true)
    setLogsError(null)
    try {
      const res = await apiClient.adminGetEmailLog({
        skip,
        limit: LOG_PAGE_SIZE,
        to_email: filters.to_email.trim() || undefined,
        template: filters.template.trim() || undefined,
        success: filters.success === 'all' ? undefined : filters.success === 'success',
      })
      setLogs(res.emails || [])
      setLogsTotal(res.total || 0)
    } catch (e: any) {
      console.error('Failed to load email log', e)
      setLogsError(e.message || 'Failed to load email log')
    } finally {
      setLogsLoading(false)
    }
  }, [])

  const applyLogFilters = () => {
    const next = { to_email: toEmailInput, template: templateInput, success: successFilter }
    setAppliedFilters(next)
    setLogSkip(0)
    loadLogs(0, next)
  }

  const goToPage = (skip: number) => {
    setLogSkip(skip)
    loadLogs(skip, appliedFilters)
  }

  useEffect(() => {
    loadEmailStatus()
    loadLogs(0, { to_email: '', template: '', success: 'all' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const logColumns: DataTableColumn<EmailLogRow>[] = [
    {
      key: 'sent_at',
      header: 'Sent At',
      render: (row) => <span className="text-sm whitespace-nowrap">{formatDateTime(row.sent_at)}</span>,
    },
    {
      key: 'to_email',
      header: 'To',
      render: (row) => <span className="text-sm font-mono">{row.to_email}</span>,
    },
    {
      key: 'subject',
      header: 'Subject',
      render: (row) => <span className="text-sm">{row.subject}</span>,
    },
    {
      key: 'template',
      header: 'Template',
      render: (row) =>
        row.template ? (
          <Badge variant="outline" className="font-mono text-xs">
            {row.template}
          </Badge>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      key: 'success',
      header: 'Success',
      render: (row) =>
        row.success ? (
          <Badge variant="default" className="gap-1 bg-green-600 hover:bg-green-600/90">
            <CheckCircle2 className="w-3 h-3" /> Sent
          </Badge>
        ) : (
          <Badge variant="destructive" className="gap-1">
            <XCircle className="w-3 h-3" /> Failed
          </Badge>
        ),
    },
    {
      key: 'error',
      header: 'Error',
      render: (row) =>
        row.error ? (
          <span
            className="text-sm text-destructive block max-w-[240px] truncate"
            title={row.error}
          >
            {row.error}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
  ]

  const pageStart = logsTotal === 0 ? 0 : logSkip + 1
  const pageEnd = Math.min(logSkip + LOG_PAGE_SIZE, logsTotal)

  // ---- Broadcast composer ----
  const [filterMode, setFilterMode] = useState<FilterMode>('all')
  const [tier, setTier] = useState('')
  const [orgId, setOrgId] = useState('')
  const [userIdsText, setUserIdsText] = useState('')
  const [subject, setSubject] = useState('')
  const [htmlBody, setHtmlBody] = useState('')

  const [preview, setPreview] = useState<BroadcastPreview | null>(null)
  const [previewedKey, setPreviewedKey] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [sendLoading, setSendLoading] = useState(false)
  const [broadcastError, setBroadcastError] = useState<string | null>(null)
  const [broadcastResult, setBroadcastResult] = useState<BroadcastResult | null>(null)

  const parsedUserIds = useMemo(
    () =>
      userIdsText
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0),
    [userIdsText]
  )

  const buildFilter = useCallback((): { subscription_tier?: string; organization_id?: string; user_ids?: string[] } | null => {
    if (filterMode === 'all') return {}
    if (filterMode === 'tier') return tier ? { subscription_tier: tier } : null
    if (filterMode === 'org') return orgId.trim() ? { organization_id: orgId.trim() } : null
    if (filterMode === 'users') return parsedUserIds.length > 0 ? { user_ids: parsedUserIds } : null
    return null
  }, [filterMode, tier, orgId, parsedUserIds])

  // A key representing the exact filter/subject/body combination — used to
  // force a fresh preview whenever the admin edits anything after previewing.
  const currentKey = useMemo(() => {
    const filter = buildFilter()
    return JSON.stringify({ filter, subject, htmlBody })
  }, [buildFilter, subject, htmlBody])

  const isStale = previewedKey !== currentKey

  const resetBroadcastForm = () => {
    setFilterMode('all')
    setTier('')
    setOrgId('')
    setUserIdsText('')
    setSubject('')
    setHtmlBody('')
    setPreview(null)
    setPreviewedKey(null)
    setBroadcastError(null)
  }

  const handlePreview = async () => {
    setBroadcastError(null)
    setBroadcastResult(null)
    const filter = buildFilter()
    if (!filter) {
      setBroadcastError('Complete the recipient filter first.')
      return
    }
    if (!subject.trim() || !htmlBody.trim()) {
      setBroadcastError('Subject and body are required.')
      return
    }
    setPreviewLoading(true)
    try {
      const res = (await apiClient.adminBroadcastEmail({
        filter,
        subject,
        html_body: htmlBody,
        confirm: false,
      })) as BroadcastPreview
      setPreview(res)
      setPreviewedKey(JSON.stringify({ filter, subject, htmlBody }))
    } catch (e: any) {
      console.error('Failed to preview broadcast', e)
      setBroadcastError(e.message || 'Failed to preview broadcast')
      setPreview(null)
      setPreviewedKey(null)
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleConfirmSend = async () => {
    const filter = buildFilter()
    if (!filter || isStale || !preview) return
    setSendLoading(true)
    setBroadcastError(null)
    try {
      const res = (await apiClient.adminBroadcastEmail({
        filter,
        subject,
        html_body: htmlBody,
        confirm: true,
      })) as BroadcastResult
      setBroadcastResult(res)
      toast({
        title: `Broadcast sent: ${res.sent.length} succeeded, ${res.failed.length} failed`,
        variant: res.failed.length > 0 ? 'destructive' : 'default',
      })
      setPreview(null)
      setPreviewedKey(null)
      // Refresh the activity log so the just-sent broadcast shows up.
      loadLogs(0, appliedFilters)
      setLogSkip(0)
    } catch (e: any) {
      console.error('Failed to send broadcast', e)
      setBroadcastError(e.message || 'Failed to send broadcast')
      toast({ title: 'Broadcast failed', description: e.message, variant: 'destructive' })
    } finally {
      setSendLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* 1. SMTP Health */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Email Configuration
          </CardTitle>
          <Button variant="outline" size="sm" onClick={loadEmailStatus} disabled={emailStatusLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${emailStatusLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {emailStatusError ? (
            <p className="text-sm text-destructive">{emailStatusError}</p>
          ) : emailStatus ? (
            <div className="space-y-4">
              <div
                className={`p-4 rounded border ${
                  emailStatus.configured
                    ? 'border-green-500/50 bg-green-50 dark:bg-green-900/10'
                    : 'border-amber-500/50 bg-amber-50 dark:bg-amber-900/10'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant={emailStatus.configured ? 'default' : 'secondary'}>
                    {emailStatus.configured ? 'Configured' : 'Not Configured'}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">{emailStatus.message}</p>
                {emailStatus.configured && (
                  <p className="text-xs mt-2 font-mono">
                    {emailStatus.host}:{emailStatus.port} • From: {emailStatus.from}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={emailTestLoading}
                  onClick={async () => {
                    setEmailTestLoading(true)
                    try {
                      const result = (await apiClient.testEmail()) as { status: string; message: string }
                      toast({
                        title: result.status === 'success' ? result.message : `Error: ${result.message}`,
                        variant: result.status === 'success' ? 'default' : 'destructive',
                      })
                    } catch (e: any) {
                      toast({ title: 'Test failed', description: e.message, variant: 'destructive' })
                    } finally {
                      setEmailTestLoading(false)
                    }
                  }}
                >
                  {emailTestLoading ? <Activity className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                  Send Test Email
                </Button>
              </div>
              {!emailStatus.configured && (
                <p className="text-xs text-muted-foreground">
                  Set SMTP_USER and SMTP_PASSWORD in your environment (.env or docker-compose). See apps/api/.env.example.
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Loading email status...</p>
          )}
        </CardContent>
      </Card>

      {/* 2. Compose Broadcast */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="w-5 h-5" />
            Compose Broadcast
          </CardTitle>
          <CardDescription>
            Preview the recipient list before sending. Any edit to the filter, subject, or body invalidates the preview
            and requires previewing again before you can send.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Recipients</Label>
            <div className="flex flex-wrap gap-2">
              {(['all', 'tier', 'org', 'users'] as FilterMode[]).map((mode) => (
                <Button
                  key={mode}
                  type="button"
                  size="sm"
                  variant={filterMode === mode ? 'default' : 'outline'}
                  onClick={() => setFilterMode(mode)}
                >
                  {mode === 'all' && 'All users'}
                  {mode === 'tier' && 'By tier'}
                  {mode === 'org' && 'By organization'}
                  {mode === 'users' && 'Specific user IDs'}
                </Button>
              ))}
            </div>

            {filterMode === 'tier' && (
              <Select value={tier} onValueChange={setTier}>
                <SelectTrigger className="max-w-xs">
                  <SelectValue placeholder="Select a subscription tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            )}

            {filterMode === 'org' && (
              <Input
                className="max-w-xs"
                placeholder="Organization ID"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
              />
            )}

            {filterMode === 'users' && (
              <Textarea
                placeholder={'user-id-1\nuser-id-2, user-id-3'}
                value={userIdsText}
                onChange={(e) => setUserIdsText(e.target.value)}
                className="font-mono text-sm min-h-[80px]"
              />
            )}
          </div>

          <div className="space-y-2">
            <Label>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Email subject" />
          </div>

          <div className="space-y-2">
            <Label>Body (HTML)</Label>
            <Textarea
              value={htmlBody}
              onChange={(e) => setHtmlBody(e.target.value)}
              placeholder="<p>Hello...</p>"
              className="min-h-[160px] font-mono text-sm"
            />
          </div>

          {broadcastError && (
            <p className="text-sm text-destructive flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" /> {broadcastError}
            </p>
          )}

          {preview && !isStale && (
            <div
              className={`p-4 rounded border space-y-2 ${
                preview.exceeds_cap
                  ? 'border-destructive/50 bg-destructive/10'
                  : 'border-border bg-muted/30'
              }`}
            >
              <p className="text-sm font-medium">
                {preview.recipient_count} recipient{preview.recipient_count === 1 ? '' : 's'} matched
              </p>
              {preview.sample_recipients.length > 0 && (
                <p className="text-xs text-muted-foreground font-mono">
                  Sample: {preview.sample_recipients.join(', ')}
                </p>
              )}
              {preview.exceeds_cap && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  Exceeds the 1000-recipient cap per broadcast — narrow the filter before sending.
                </p>
              )}
            </div>
          )}

          {broadcastResult && (
            <div className="p-4 rounded border border-border bg-muted/30 space-y-1">
              <p className="text-sm font-medium">
                Sent: {broadcastResult.sent.length} · Failed: {broadcastResult.failed.length}
              </p>
              {broadcastResult.failed.length > 0 && (
                <ul className="text-xs text-destructive space-y-0.5">
                  {broadcastResult.failed.map((f) => (
                    <li key={f.email}>
                      {f.email}: {f.error}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={handlePreview} disabled={previewLoading}>
              {previewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Preview
            </Button>
            <Button
              type="button"
              onClick={handleConfirmSend}
              disabled={!preview || isStale || preview.exceeds_cap || sendLoading}
            >
              {sendLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Confirm &amp; Send
            </Button>
            <Button type="button" variant="ghost" onClick={resetBroadcastForm} disabled={previewLoading || sendLoading}>
              Reset
            </Button>
          </div>
          {preview && isStale && (
            <p className="text-xs text-muted-foreground">
              Filter, subject, or body changed since the last preview — preview again before sending.
            </p>
          )}
        </CardContent>
      </Card>

      {/* 3. Email Activity Log */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Email Activity Log</CardTitle>
          <Button variant="outline" size="sm" onClick={() => loadLogs(logSkip, appliedFilters)} disabled={logsLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${logsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Recipient</Label>
              <Input
                className="w-56"
                placeholder="Search by email"
                value={toEmailInput}
                onChange={(e) => setToEmailInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyLogFilters()}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Template</Label>
              <Input
                className="w-48"
                placeholder="e.g. password_reset"
                value={templateInput}
                onChange={(e) => setTemplateInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyLogFilters()}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <Select value={successFilter} onValueChange={(v) => setSuccessFilter(v as SuccessFilter)}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" onClick={applyLogFilters} disabled={logsLoading}>
              Apply Filters
            </Button>
          </div>

          {logsError && <p className="text-sm text-destructive">{logsError}</p>}

          <DataTable<EmailLogRow>
            columns={logColumns}
            rows={logs}
            getRowId={(row) => row.id}
            isLoading={logsLoading}
            emptyMessage="No emails logged yet."
          />

          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {logsTotal === 0 ? 'No results' : `Showing ${pageStart}-${pageEnd} of ${logsTotal}`}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={logSkip === 0 || logsLoading}
                onClick={() => goToPage(Math.max(0, logSkip - LOG_PAGE_SIZE))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={logSkip + LOG_PAGE_SIZE >= logsTotal || logsLoading}
                onClick={() => goToPage(logSkip + LOG_PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
